import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { QUIZ_STEPS } from './data/quizData';
import QuestionStep from './components/QuestionStep';
import ContactStep from './components/ContactStep';
import ResultStep from './components/ResultStep';
import SafeIcon from './common/SafeIcon';
import { FiArrowLeft } from 'react-icons/fi';

export default function App() {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [direction, setDirection] = useState(1); // 1 for forward, -1 for backward

  const isResultStep = currentStepIndex === QUIZ_STEPS.length;
  const currentStep = QUIZ_STEPS[currentStepIndex];

  // Calculate progress excluding the result step
  const progressPercent = isResultStep 
    ? 100 
    : ((currentStepIndex) / QUIZ_STEPS.length) * 100;

  const handleNext = (stepId, value) => {
    setAnswers(prev => ({ ...prev, [stepId]: value }));
    setDirection(1);
    
    // Slight delay for better UX on questions
    if (currentStep.type === 'question') {
      setTimeout(() => {
        setCurrentStepIndex(prev => prev + 1);
      }, 300);
    } else {
      setCurrentStepIndex(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStepIndex > 0 && !isResultStep) {
      setDirection(-1);
      setCurrentStepIndex(prev => prev - 1);
    }
  };

  const variants = {
    enter: (direction) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 50 : -50,
      opacity: 0
    })
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5] bg-gradient-to-br from-[#F4F4F5] to-[#EDEDF0] text-[#1A1A1A] font-sans selection:bg-amber-500/20 flex flex-col">
      
      {/* Header & Progress */}
      {!isResultStep && (
        <header className="fixed top-0 left-0 right-0 z-50 bg-white/60 backdrop-blur-md border-b border-zinc-200/50">
          <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
            <button 
              onClick={handleBack}
              className={`p-2 -ml-2 rounded-lg text-zinc-400 hover:text-[#1A1A1A] transition-colors ${currentStepIndex === 0 ? 'invisible' : ''}`}
            >
              <SafeIcon icon={FiArrowLeft} className="text-xl" />
            </button>
            <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-[0.2em]">
              Step {currentStepIndex + 1} of {QUIZ_STEPS.length}
            </div>
            <div className="w-8" /> {/* Spacer for alignment */}
          </div>
          {/* Progress Bar Container */}
          <div className="w-full h-[2px] bg-zinc-200/50">
            <motion.div 
              className="h-full bg-amber-500"
              initial={{ width: 0 }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            />
          </div>
        </header>
      )}

      {/* Main Content Area */}
      <main className="flex-1 flex items-center justify-center p-4 pt-24 pb-12 overflow-hidden relative">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentStepIndex}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ type: "tween", ease: "easeInOut", duration: 0.3 }}
            className="w-full absolute px-4"
          >
            {isResultStep ? (
              <ResultStep answers={answers} />
            ) : currentStep.type === 'question' ? (
              <QuestionStep 
                step={currentStep} 
                currentAnswer={answers[currentStep.id]}
                onAnswer={handleNext} 
              />
            ) : currentStep.type === 'contact' ? (
              <ContactStep 
                step={currentStep}
                currentData={answers[currentStep.id]}
                onComplete={handleNext}
              />
            ) : null}
          </motion.div>
        </AnimatePresence>
      </main>

    </div>
  );
}